import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { CheckCircle, Loader, RotateCcw } from 'lucide-react';
import { velocityApi } from '../../services/api';
import {
  VELOCITY_DEFAULT_BOX,
  buildVelocityBox,
  velocityErrorMessage,
  velocityTotalCharges,
} from '../../utils/velocity';

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : '—';
};

/**
 * Reverse pickup — customer se uthega, hamare warehouse par aayega.
 * Carriers return lane ke hote hain (serviceability?type=return).
 * Order DELIVERED hi rehta hai; return warehouse pahunchne par backend
 * khud RETURNED kar deta hai.
 */
const VelocityReturnModal = ({ open, order, onClose, onCreated }) => {
  const orderId = order?.id;
  const [loading, setLoading] = useState(true);
  const [serviceability, setServiceability] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [carrierId, setCarrierId] = useState('');
  const [weightKg, setWeightKg] = useState(VELOCITY_DEFAULT_BOX.weightKg);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setServiceability(await velocityApi.getServiceability(orderId, 'return'));
    } catch (err) {
      setLoadError(velocityErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!open) return;
    setCarrierId('');
    setWeightKg(VELOCITY_DEFAULT_BOX.weightKg);
    setBusy(false);
    setError(null);
    setResult(null);
    load();
  }, [open, load]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = buildVelocityBox({ weightKg });
      if (carrierId) body.carrierId = carrierId;
      const shipment = await velocityApi.createReturn(orderId, body);
      setResult(shipment);
      onCreated?.(shipment);
    } catch (err) {
      setError(velocityErrorMessage(err, { action: 'ship' }));
    } finally {
      setBusy(false);
    }
  };

  const carriers = serviceability?.carriers || [];
  const notServiceable = !!serviceability && serviceability.serviceable === false;
  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200';

  return (
    <Modal
      title={result ? '✅ Return booked' : '↩ Create return'}
      open={open}
      onCancel={() => !busy && onClose?.()}
      footer={null}
      width={520}
      closable={!busy}
      maskClosable={!busy}
      destroyOnClose
    >
      {result ? (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-green-50 border border-green-200 flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
            <div>
              <p className="font-bold text-green-800">Pickup scheduled with {result.courierName || 'Velocity'}</p>
              <p className="mt-1 text-sm">
                Return AWB <span className="font-mono font-bold">{result.awbCode || 'pending'}</span>
              </p>
              {velocityTotalCharges(result.charges) !== null && (
                <p className="mt-1 text-sm text-green-800">
                  Reverse charges {money(velocityTotalCharges(result.charges))}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button type="button" className="px-6 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            The courier collects the parcel from the customer and delivers it to our warehouse. The order stays
            DELIVERED until the return arrives.
          </p>

          {loading && (
            <div className="py-8 flex flex-col items-center gap-3 text-gray-500">
              <Loader className="w-6 h-6 animate-spin" />
              <span className="text-sm">Checking return serviceability…</span>
            </div>
          )}

          {!loading && loadError && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              {loadError}
              <button type="button" className="ml-3 underline font-semibold" onClick={load}>
                Retry
              </button>
            </div>
          )}

          {!loading && notServiceable && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              {serviceability?.message || serviceability?.error || 'This pincode is not serviceable for returns.'}
            </div>
          )}

          {!loading && !loadError && !notServiceable && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-gray-600">
                Carrier
                <select
                  className={`${inputCls} mt-1`}
                  value={carrierId}
                  onChange={(e) => setCarrierId(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Auto (Velocity chooses)</option>
                  {carriers.map((c) => (
                    <option key={c.carrierId} value={c.carrierId}>
                      {c.carrierName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-gray-600">
                Weight (kg)
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className={`${inputCls} mt-1`}
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  disabled={busy}
                />
              </label>
            </div>
          )}

          {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-5 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 disabled:opacity-50"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              onClick={submit}
              disabled={busy || loading || notServiceable || !!loadError}
            >
              {busy ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {busy ? 'Booking…' : 'Create return'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VelocityReturnModal;
